/**
 * Include THREE MONTHS of Managed Private AI free with every hardware system.
 *
 * Owner decision 2026-08-12 (supersedes first-month-free): every buyer gets three
 * months, and NO CREDIT CARD IS REQUIRED for it.
 *
 * WHY THIS IS NOT A SUBSCRIPTION: a Shopify selling plan stores a payment method and
 * auto-charges when the free cycles end. That is a negative option and it flatly
 * contradicts "no credit card required". So we add a $0, NON-recurring line item
 * (product managed-private-ai-trial) that simply expires. Nothing is stored, nothing
 * auto-charges, there is nothing to cancel. Do not "simplify" this back onto the
 * selling plan without changing the promise first.
 *
 * BEHAVIOUR:
 *   - Adds the $0 trial line when the cart holds hardware and no managed line.
 *   - Marks it with cart attribute _managed_auto=1.
 *   - If it later disappears while _managed_auto=1, the buyer removed it deliberately:
 *     set _managed_optout=1 and never add it again. Re-adding a removed line is the
 *     dark pattern this must not become.
 *   - Leaves any hand-picked paid plan (Essential/Complete) alone.
 *   - Never runs on checkout/order-status pages.
 *
 * Disclosure lives beside the buy button (dtp-care-attach). If the offer changes,
 * change that copy in the same commit.
 */
(function () {
  'use strict';

  var MANAGED_TRIAL = 54654508007703;   // $0, no selling plan, 3 months included
  var FIRST_YEAR = 54717061136663;          // $0, 12-month grant (Private Suite + Limited 16GB)
  var YEAR_HARDWARE = [54717061038359, 54717061071127, 54605786841367, 54605787136279];
  var MANAGED_ESSENTIAL = 54641184047383;
  var MANAGED_COMPLETE = 54641184080151;
  var HARDWARE = [54717061038359, 54717061071127, 54605786841367, 54605787136279, 53521501421847, 53521501389079, 54534759776535, 54534759809303];

  var busy = false;

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function hasAny(cart, ids) {
    return (cart.items || []).some(function (i) { return ids.indexOf(i.variant_id) !== -1; });
  }

  function setAttrs(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  function reconcile() {
    if (busy) return Promise.resolve();
    if (/\/(checkouts|orders)\//.test(window.location.pathname)) return Promise.resolve();
    busy = true;

    return getCart().then(function (cart) {
      var attrs = cart.attributes || {};
      var hardware = hasAny(cart, HARDWARE);
      var managed = hasAny(cart, [MANAGED_TRIAL, FIRST_YEAR, MANAGED_ESSENTIAL, MANAGED_COMPLETE]);
      var wantYear = hasAny(cart, YEAR_HARDWARE);
      // a year-included system upgrades an auto-added 3-month line to the full year
      if (wantYear && hasAny(cart, [MANAGED_TRIAL]) && !hasAny(cart, [FIRST_YEAR]) && attrs._managed_auto === '1') {
        var line = (cart.items || []).filter(function (i) { return i.variant_id === MANAGED_TRIAL; })[0];
        return fetch('/cart/change.js', { method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }) }).then(function () {
          return fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: FIRST_YEAR, quantity: 1 }] }) });
        }).then(function () { if (/\/cart/.test(window.location.pathname)) window.location.reload(); });
      }

      // Buyer removed the auto-added line: respect it permanently.
      if (!managed && attrs._managed_auto === '1') {
        return setAttrs({ _managed_auto: '', _managed_optout: '1' });
      }
      if (!hardware || managed || attrs._managed_optout === '1') return null;

      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: wantYear ? FIRST_YEAR : MANAGED_TRIAL, quantity: 1 }]
        })
      }).then(function (r) {
        if (!r.ok) return null;
        return setAttrs({ _managed_auto: '1' }).then(function () {
          document.dispatchEvent(new CustomEvent('dtp:managed-included'));
          // Refresh cart UI only where the change is visible to the buyer.
          if (/\/cart/.test(window.location.pathname)) window.location.reload();
        });
      });
    }).catch(function () {
      // Never block a purchase because this failed.
      return null;
    }).then(function (v) { busy = false; return v; });
  }

  function schedule() { setTimeout(reconcile, 350); }

  document.addEventListener('DOMContentLoaded', schedule);

  // Horizon dispatches cart events; also catch raw cart writes from any source.
  ['cart:updated', 'cart:refresh', 'variant:added'].forEach(function (e) {
    document.addEventListener(e, schedule);
  });

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartWrite = /\/cart\/(add|change|update)(\.js)?/.test(url) &&
      !/_managed_reconcile/.test(url);
    return origFetch.apply(this, arguments).then(function (res) {
      if (isCartWrite && !busy) schedule();
      return res;
    });
  };
})();
